// Per-person payments (Planning/PersonPayments.md). A payroll report is paid
// per LINE — one person's pay on the gig, or one owed fee — so a person can be
// paid across several gigs without marking their co-workers paid. Pure
// functions: what a person is owed, what a payment covers, how marking lines
// paid moves a report's state, and the statement's per-gig blocks.
import type {
  PayrollEntry,
  PayrollFee,
  PayrollPaidMarker,
  PayrollPaymentLine,
  PayrollReport,
  PayrollState,
} from './types.js';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

type ReportLike = Pick<PayrollReport, 'gigId' | 'orgId' | 'gigName' | 'state' | 'entries' | 'fees'>;

// A report paid wholesale (the gig-wide "Mark paid", or any report from before
// per-line tracking) has state PAID with no markers on its lines — every line
// of it counts as paid.
function linePaid(report: Pick<PayrollReport, 'state'>, line: { paid?: PayrollPaidMarker }): boolean {
  return report.state === 'PAID' || !!line.paid;
}

function entryLine(report: ReportLike, e: PayrollEntry): PayrollPaymentLine {
  return {
    gigId: report.gigId,
    gigName: report.gigName,
    bookingId: e.bookingId,
    kind: 'work',
    role: e.role,
    label: e.label,
    rateType: e.rateType,
    rate: e.rate,
    workedHours: e.workedHours,
    days: e.days,
    amount: e.grossPay,
    ...(e.sharedWith?.length ? { sharedWith: e.sharedWith } : {}),
  };
}

function feeLine(report: ReportLike, f: PayrollFee): PayrollPaymentLine {
  return { gigId: report.gigId, gigName: report.gigName, bookingId: f.bookingId, kind: 'fee', role: f.role, amount: f.amount, reason: f.reason };
}

// The person's lines on this report that are still unpaid.
export function unpaidLinesForPerson(report: ReportLike, personId: string): PayrollPaymentLine[] {
  return [
    ...report.entries.filter(e => e.personId === personId && !linePaid(report, e)).map(e => entryLine(report, e)),
    ...report.fees.filter(f => f.personId === personId && !linePaid(report, f)).map(f => feeLine(report, f)),
  ];
}

export interface OwedGig {
  orgId: string;
  gigId: string;
  gigName: string;
  amount: number;
  workedHours: number;
  lines: PayrollPaymentLine[];
}

export interface OwedPerson {
  personId: string;
  name: string;
  total: number;
  workedHours: number;
  gigs: OwedGig[];
}

export interface OwedSummary {
  // Sorted by name. Only people with something owed on a FINALIZED report.
  people: OwedPerson[];
  // Total across reports that are still DRAFT — real money coming, but not
  // final, so it's kept separate rather than hidden or mixed in.
  draftTotal: number;
}

export function owedByPerson(reports: ReportLike[] & Array<Pick<PayrollReport, 'totalCost'>>): OwedSummary {
  const byPerson = new Map<string, OwedPerson>();
  const names = new Map<string, string>();
  let draftTotal = 0;

  for (const report of reports) {
    if (report.state === 'DRAFT') {
      draftTotal += report.totalCost;
      continue;
    }
    if (report.state !== 'PROCESSED') continue; // PAID: nothing owed

    for (const e of report.entries) names.set(e.personId, e.name);
    for (const f of report.fees) names.set(f.personId, f.name);
    const personIds = new Set([...report.entries.map(e => e.personId), ...report.fees.map(f => f.personId)]);

    for (const personId of personIds) {
      const lines = unpaidLinesForPerson(report, personId);
      if (lines.length === 0) continue;
      const amount = round2(lines.reduce((t, l) => t + l.amount, 0));
      const workedHours = round2(lines.reduce((t, l) => t + (l.workedHours ?? 0), 0));
      let person = byPerson.get(personId);
      if (!person) {
        person = { personId, name: names.get(personId) ?? personId, total: 0, workedHours: 0, gigs: [] };
        byPerson.set(personId, person);
      }
      person.gigs.push({ orgId: report.orgId, gigId: report.gigId, gigName: report.gigName, amount, workedHours, lines });
      person.total = round2(person.total + amount);
      person.workedHours = round2(person.workedHours + workedHours);
    }
  }

  const people = [...byPerson.values()].sort((a, b) => a.name.localeCompare(b.name) || a.personId.localeCompare(b.personId));
  for (const p of people) p.gigs.sort((a, b) => a.gigName.localeCompare(b.gigName));
  return { people, draftTotal: round2(draftTotal) };
}

// A finalized report is PAID exactly when every one of its lines is paid;
// otherwise it stays PROCESSED (partly paid or not at all).
export function reportPaidState(report: Pick<PayrollReport, 'state' | 'entries' | 'fees'>): Extract<PayrollState, 'PROCESSED' | 'PAID'> {
  const all = [...report.entries, ...report.fees];
  return all.length > 0 && all.every(l => linePaid(report, l)) ? 'PAID' : 'PROCESSED';
}

// True once any money has gone out against the report — such a report can't be
// reopened, since that would invalidate what's already been paid.
export function hasAnyPaidLine(report: Pick<PayrollReport, 'state' | 'entries' | 'fees'>): boolean {
  return report.state === 'PAID' || [...report.entries, ...report.fees].some(l => !!l.paid);
}

export interface PaidTotals {
  paid: number;
  unpaid: number;
}

export function reportPaidTotals(report: Pick<PayrollReport, 'state' | 'entries' | 'fees'>): PaidTotals {
  let paid = 0;
  let unpaid = 0;
  for (const e of report.entries) (linePaid(report, e) ? (paid += e.grossPay) : (unpaid += e.grossPay));
  for (const f of report.fees) (linePaid(report, f) ? (paid += f.amount) : (unpaid += f.amount));
  return { paid: round2(paid), unpaid: round2(unpaid) };
}

// Marks lines paid — the person's own, or (personId omitted) every unpaid line
// on the report — and returns the updated lines plus the report's new state.
// Lines already paid are left exactly as they were.
export function markLinesPaid<R extends Pick<PayrollReport, 'state' | 'entries' | 'fees'>>(
  report: R,
  marker: PayrollPaidMarker,
  personId?: string,
): { entries: PayrollEntry[]; fees: PayrollFee[]; state: Extract<PayrollState, 'PROCESSED' | 'PAID'>; marked: number } {
  let marked = 0;
  const mark = <T extends { personId: string; paid?: PayrollPaidMarker }>(line: T): T => {
    if (linePaid(report, line) || (personId && line.personId !== personId)) return line;
    marked += 1;
    return { ...line, paid: marker };
  };
  const entries = report.entries.map(mark);
  const fees = report.fees.map(mark);
  return { entries, fees, state: reportPaidState({ state: 'PROCESSED', entries, fees }), marked };
}

// ---- statement ---------------------------------------------------------------

export interface StatementBlock {
  gigId: string;
  gigName: string;
  lines: PayrollPaymentLine[];
  subtotal: number;
}

// Groups a payment's lines by gig (in the order gigs first appear), each with
// its subtotal — what the statement PDF prints. Subtotals always sum to the
// payment total.
export function statementBlocks(lines: PayrollPaymentLine[]): StatementBlock[] {
  const blocks = new Map<string, StatementBlock>();
  for (const line of lines) {
    let block = blocks.get(line.gigId);
    if (!block) {
      block = { gigId: line.gigId, gigName: line.gigName, lines: [], subtotal: 0 };
      blocks.set(line.gigId, block);
    }
    block.lines.push(line);
    block.subtotal = round2(block.subtotal + line.amount);
  }
  return [...blocks.values()];
}

export function paymentTotal(lines: PayrollPaymentLine[]): number {
  return round2(lines.reduce((t, l) => t + l.amount, 0));
}
